import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { History, Hammer, Play, FlaskConical } from 'lucide-react';
import { TabsContainer } from './styled';
import { useWorkflowTrigger } from '@/hooks';
import getDefaultPlaygroundMode from '@/pages/playground/helpers/getDefaultPlaygroundMode';

function TopbarOptions() {
    const location = useLocation();
    const navigate = useNavigate();
    const { agentId, workflowId, versionId } = useParams();

    const { trigger } = useWorkflowTrigger({
        workflowId: workflowId ?? '',
        versionId: versionId ?? null,
    });

    // Check if we're on a workflow-related route
    const isWorkflowRoute = location.pathname.includes('/workflows');

    // Only show tabs when on specific workflow routes (not main workflows list)
    const isOnSpecificWorkflow = location.pathname.includes('/versions/');

    if (!isWorkflowRoute || !isOnSpecificWorkflow) {
        return null;
    }

    // Determine current tab based on URL
    const isBuilderMode = location.pathname.includes('/builder');
    const isHistoryMode = location.pathname.includes('/history');
    const isTestingMode = location.pathname.includes('/testing');
    const isPlaygroundMode = /\/versions\/[^/]+\/(graph|chat)/.test(location.pathname);

    const handleNavigateToBuilder = () => {
        if (isBuilderMode && workflowId && versionId) {
            return;
        }

        if (!workflowId || !versionId) {
            return;
        }

        navigate(`/agents/${agentId}/workflows/${workflowId}/versions/${versionId}/builder`);
    };

    const handleNavigateToHistory = () => {
        if (isHistoryMode && workflowId && versionId) {
            return;
        }

        if (!workflowId || !versionId) {
            return;
        }

        navigate(`/agents/${agentId}/workflows/${workflowId}/versions/${versionId}/history`);
    };

    const handleNavigateToTesting = () => {
        if (isTestingMode && workflowId && versionId) {
            return;
        }

        if (!workflowId || !versionId) {
            return;
        }

        navigate(`/agents/${agentId}/workflows/${workflowId}/versions/${versionId}/testing`);
    };

    const handleNavigateToPlayground = () => {
        if (isPlaygroundMode && workflowId && versionId) {
            return;
        }

        if (!workflowId || !versionId) {
            return;
        }

        const defaultMode = getDefaultPlaygroundMode({ trigger });
        navigate(`/agents/${agentId}/workflows/${workflowId}/versions/${versionId}/${defaultMode}`);
    };

    return (
        <TabsContainer>
            <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToPlayground}
                disabled={!workflowId || !versionId}
                className={`font-medium transition-all duration-200 gap-2 ${
                    isPlaygroundMode
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground`}
            >
                <Play size={16} />
                Playground
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToBuilder}
                disabled={!workflowId || !versionId}
                className={`font-medium transition-all duration-200 gap-2 ${
                    isBuilderMode
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground`}
            >
                <Hammer size={16} />
                Builder
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToHistory}
                className={`font-medium transition-all duration-200 gap-2 ${
                    isHistoryMode
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
            >
                <History size={16} />
                History
            </Button>
            <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToTesting}
                disabled={!workflowId || !versionId}
                className={`font-medium transition-all duration-200 gap-2 ${
                    isTestingMode
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground`}
            >
                <FlaskConical size={16} />
                Testing
            </Button>
        </TabsContainer>
    );
}

export default TopbarOptions;
